import React, { useState, useEffect, useCallback } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Search, Plus, Building2, Phone, Mail, ChevronRight, Users, Loader2 } from 'lucide-react';
import { getEmpresas, createEmpresa, updateEmpresa, scraperRut } from '../../lib/gestion4satApi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function G4sEmpresasPage() {
  const [empresas, setEmpresas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editEmpresa, setEditEmpresa] = useState(null);
  const [form, setForm] = useState({
    nombre_cliente: '', razon_social: '', rut: '', direccion: '',
    comuna: '', ciudad: '', giro: '', telefono: '',
    precio_mensualidad: 10000, tipo_servicio: 'gps_solo', notas: '',
    payment_conditions: '30',
  });
  const navigate = useNavigate();
  const [buscandoRut, setBuscandoRut] = useState(false);

  const buscarRutSII = async (rut) => {
    if (!rut || rut.length < 8) return;
    setBuscandoRut(true);
    try {
      const res = await scraperRut(rut);
      if (res.razon_social) {
        setForm(f => ({
          ...f,
          razon_social: f.razon_social || res.razon_social,
          nombre_cliente: f.nombre_cliente || res.razon_social,
        }));
        toast.success(`SII: ${res.razon_social}`, { description: `Fuente: ${res.source}` });
      } else {
        toast.info('RUT no encontrado en SII');
      }
    } catch (e) {
      toast.error('Error consultando SII');
    }
    setBuscandoRut(false);
  };

  const load = useCallback(async () => {
    try {
      const d = await getEmpresas({ q: search, estado: filtroEstado, limit: 200 });
      setEmpresas(d.empresas);
      setTotal(d.total);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [search, filtroEstado]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    try {
      if (editEmpresa) {
        await updateEmpresa(editEmpresa.id, form);
        toast.success('Empresa actualizada');
      } else {
        await createEmpresa(form);
        toast.success('Empresa creada');
      }
      setShowForm(false);
      setEditEmpresa(null);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const openEdit = (emp) => {
    setEditEmpresa(emp);
    setForm({
      nombre_cliente: emp.nombre_cliente || '',
      razon_social: emp.razon_social || '',
      rut: emp.rut || '',
      direccion: emp.direccion || '',
      comuna: emp.comuna || '',
      ciudad: emp.ciudad || '',
      giro: emp.giro || '',
      telefono: emp.telefono || '',
      precio_mensualidad: emp.precio_mensualidad || 10000,
      tipo_servicio: emp.tipo_servicio || 'gps_solo',
      notas: emp.notas || '',
      payment_conditions: emp.payment_conditions || '30',
    });
    setShowForm(true);
  };

  const openCreate = () => {
    setEditEmpresa(null);
    setForm({
      nombre_cliente: '', razon_social: '', rut: '', direccion: '',
      comuna: '', ciudad: '', giro: '', telefono: '',
      precio_mensualidad: 10000, tipo_servicio: 'gps_solo', notas: '',
      payment_conditions: '30',
    });
    setShowForm(true);
  };

  const tipoLabel = {
    gps_solo: 'GPS Solo',
    gps_arriendo: 'GPS + Arriendo',
    camaras: 'Cámaras',
  };

  return (
    <G4sLayout>
      <div className="space-y-4" data-testid="g4s-empresas-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Empresas</h1>
            <p className="text-sm text-muted-foreground">{total} empresas registradas</p>
          </div>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-crear-empresa">
            <Plus className="h-4 w-4 mr-2" /> Nueva Empresa
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por RUT, razón social..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-empresas"
            />
          </div>
          <Select value={filtroEstado} onValueChange={setFiltroEstado}>
            <SelectTrigger className="w-40" data-testid="filtro-estado">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="activo">Activos</SelectItem>
              <SelectItem value="inactivo">Inactivos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-semibold">RUT</th>
                  <th className="text-left p-3 font-semibold">Razón Social</th>
                  <th className="text-left p-3 font-semibold hidden md:table-cell">Tipo Servicio</th>
                  <th className="text-right p-3 font-semibold hidden sm:table-cell">Mensualidad</th>
                  <th className="text-center p-3 font-semibold">Dispositivos</th>
                  <th className="text-center p-3 font-semibold hidden sm:table-cell">Estado</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b"><td colSpan={7} className="p-3"><Skeleton className="h-8 w-full" /></td></tr>
                  ))
                ) : empresas.length === 0 ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">Sin empresas. Importa desde Facto o crea manualmente.</td></tr>
                ) : (
                  empresas.map(emp => (
                    <tr key={emp.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/gestion4sat/empresas/${emp.id}`)}>
                      <td className="p-3 font-mono text-xs">{emp.rut_formateado || emp.rut}</td>
                      <td className="p-3 font-medium">{emp.razon_social}</td>
                      <td className="p-3 hidden md:table-cell">
                        <Badge variant="outline" className="text-xs">{tipoLabel[emp.tipo_servicio] || emp.tipo_servicio}</Badge>
                      </td>
                      <td className="p-3 text-right font-mono hidden sm:table-cell">${(emp.precio_mensualidad || 0).toLocaleString('es-CL')}</td>
                      <td className="p-3 text-center">
                        <Badge variant="secondary" className="font-mono">{emp.dispositivos_count || 0}</Badge>
                      </td>
                      <td className="p-3 text-center hidden sm:table-cell">
                        <Badge className={emp.estado === 'activo' ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-red-500/10 text-red-600 border-red-500/20'}>
                          {emp.estado}
                        </Badge>
                      </td>
                      <td className="p-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}</DialogTitle>
              <DialogDescription>
                {editEmpresa ? 'Modifica los datos de la empresa' : 'Ingresa el RUT para auto-completar datos desde SII'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>RUT</Label>
                  <div className="flex gap-2">
                    <Input
                      value={form.rut}
                      onChange={e => setForm({...form, rut: e.target.value})}
                      onBlur={() => !editEmpresa && form.rut && buscarRutSII(form.rut)}
                      placeholder="12.345.678-9"
                      disabled={!!editEmpresa}
                      data-testid="input-rut"
                    />
                    {!editEmpresa && (
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => buscarRutSII(form.rut)}
                        disabled={buscandoRut || !form.rut}
                        title="Consultar SII"
                        data-testid="btn-consultar-sii"
                      >
                        {buscandoRut ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <Label>Tipo Servicio</Label>
                  <Select value={form.tipo_servicio} onValueChange={v => setForm({...form, tipo_servicio: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gps_solo">GPS Solo ($10.000)</SelectItem>
                      <SelectItem value="gps_arriendo">GPS + Arriendo ($15.000)</SelectItem>
                      <SelectItem value="camaras">Cámaras ($22.000)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Nombre Cliente</Label>
                <Input value={form.nombre_cliente} onChange={e => setForm({...form, nombre_cliente: e.target.value})} data-testid="input-nombre" />
              </div>
              <div>
                <Label>Razón Social</Label>
                <Input value={form.razon_social} onChange={e => setForm({...form, razon_social: e.target.value})} data-testid="input-razon" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Dirección</Label>
                  <Input value={form.direccion} onChange={e => setForm({...form, direccion: e.target.value})} />
                </div>
                <div>
                  <Label>Comuna</Label>
                  <Input value={form.comuna} onChange={e => setForm({...form, comuna: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ciudad</Label>
                  <Input value={form.ciudad} onChange={e => setForm({...form, ciudad: e.target.value})} />
                </div>
                <div>
                  <Label>Teléfono</Label>
                  <Input value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Giro</Label>
                <Input value={form.giro} onChange={e => setForm({...form, giro: e.target.value})} />
              </div>
              <div>
                <Label>Mensualidad Neta ($)</Label>
                <Input type="number" value={form.precio_mensualidad} onChange={e => setForm({...form, precio_mensualidad: parseInt(e.target.value) || 0})} data-testid="input-precio" />
              </div>
              <div>
                <Label>Condición de Pago (SII)</Label>
                <Select value={form.payment_conditions} onValueChange={v => setForm({...form, payment_conditions: v})}>
                  <SelectTrigger data-testid="select-payment-conditions"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Contado (0 días)</SelectItem>
                    <SelectItem value="15">15 días</SelectItem>
                    <SelectItem value="30">30 días</SelectItem>
                    <SelectItem value="60">60 días</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-guardar-empresa">
                {editEmpresa ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </G4sLayout>
  );
}
